import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Building2, PoundSterling, TrendingUp } from 'lucide-react';
import { getPlatformProfitOverview, type ProfitByCustomer } from '../api/reporting';
import { PageHeader } from '../components/ui/PageHeader';
import { Card, CardBody } from '../components/ui/Card';
import { Table, Thead, Th, Tr, Td } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import { EmptyState } from '../components/ui/EmptyState';

function money(value: number) {
  return `£${value.toFixed(2)}`;
}

function StatCard({ icon: Icon, label, value }: { icon: typeof Building2; label: string; value: string }) {
  return (
    <Card>
      <CardBody className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
          <p className="mt-0.5 text-lg font-semibold text-slate-900">{value}</p>
        </div>
      </CardBody>
    </Card>
  );
}

// MSP Admin only (route-guarded in App.tsx) - platform-wide revenue vs
// Oakford's cost basis, with a per-customer breakdown. Never reachable by
// a customer-side role; the underlying API is @Roles(MSP_ADMIN)-only too,
// so this is defence in depth, not the only guard.
export function ProfitLossPage() {
  const query = useQuery({ queryKey: ['profit-loss-overview'], queryFn: getPlatformProfitOverview });

  return (
    <div>
      <PageHeader
        title="Profit &amp; loss"
        description="Revenue billed to date against Oakford's cost basis, across every customer. Internal only."
      />

      {query.isLoading ? (
        <Spinner />
      ) : query.data ? (
        <>
          <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-5">
            <StatCard icon={PoundSterling} label="Revenue so far" value={money(query.data.revenueSoFar)} />
            <StatCard icon={PoundSterling} label="Revenue total" value={money(query.data.revenueTotal)} />
            <StatCard icon={PoundSterling} label="Cost" value={money(query.data.cost)} />
            <StatCard icon={TrendingUp} label="Profit" value={money(query.data.profit)} />
            <StatCard icon={TrendingUp} label="Margin" value={`${query.data.marginPercent.toFixed(1)}%`} />
          </div>
          <p className="mb-6 -mt-3 text-xs text-slate-400">
            Revenue so far is what's actually been invoiced. Revenue total adds what's still to come from active
            managed items and contracts. Cost, profit and margin are based on revenue so far only.
          </p>

          {query.data.missingCostRevenue > 0 && (
            <div className="mb-4">
              <Badge tone="amber">
                {money(query.data.missingCostRevenue)} of billed revenue has no cost basis set on its master catalog
                item - true profit is higher than shown until those items' default costs are filled in.
              </Badge>
            </div>
          )}

          <Card>
            {query.data.byCustomer.length === 0 ? (
              <EmptyState icon={Building2} title="Nothing invoiced yet" />
            ) : (
              <Table>
                <Thead>
                  <tr>
                    <Th>Customer</Th>
                    <Th>Revenue so far</Th>
                    <Th>Revenue total</Th>
                    <Th>Cost</Th>
                    <Th>Profit</Th>
                    <Th>Margin</Th>
                  </tr>
                </Thead>
                <tbody>
                  {query.data.byCustomer.map((row: ProfitByCustomer) => (
                    <Tr key={row.customerOrganisationId}>
                      <Td className="font-medium text-slate-900">
                        <Link to={`/customers/${row.customerOrganisationId}`} className="hover:underline">
                          {row.name}
                        </Link>
                      </Td>
                      <Td>{money(row.revenueSoFar)}</Td>
                      <Td>{money(row.revenueTotal)}</Td>
                      <Td>{money(row.cost)}</Td>
                      <Td className={row.profit < 0 ? 'text-red-600' : ''}>{money(row.profit)}</Td>
                      <Td>{row.marginPercent.toFixed(1)}%</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </>
      ) : null}
    </div>
  );
}
